<script setup lang="ts">
  import { AplosFile } from '@/components/FileManager/models/AplosFile'
  import { Plot } from '@/components/Plotting/models/Plot'
  import Plotting from '@/components/Plotting/Plotting.vue'
  import Tables from '@/components/Tables/Tables.vue'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { computed, h, nextTick, ref, watch } from 'vue'
  import { AnalysisWizardModel } from '../models/AnalysisWizardModel'
  import { Button, message, Modal, Select, SelectOption } from 'ant-design-vue'
  import { Table } from '@/components/Tables/models/Table'
  import { WizardStepStatusType } from '@/shared/Wizard/models/WizardStep'
  import { AnalysisWizardNavigationService } from '../services/AnalysisWizardNavigationService'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { toast } from 'vue-sonner'
  import { GetExecutionOutputPackageArgs } from '@/shared/Executions/models/GetExecutionOutputPackageArgs'
  import { useUser } from '@/composables/useUser'
  import { downloadFile, extractFilename } from '@/utils/download'
  import { useWindowSize } from '@/composables/useWindowSize'
  import {
    BookText,
    ChevronRight,
    Download,
    FileCheck,
    FileCogIcon,
    Loader2
  } from 'lucide-vue-next'
  import { PlottingService } from '@/components/Plotting/services/PlottingService'
  import { QueuePlotsArgs } from '@/shared/Executions/models/QueuePlotsArgs'
  import { QueueTablesArgs } from '@/shared/Executions/models/QueueTablesArgs'
  import { TablesService } from '@/components/Tables/services/TablesService'
  import { AnalysisWizardService } from '../services/AnalysisWizardService'
  import { QueueExecutionArgs } from '@/shared/Executions/models/QueueExecutionArgs'
  import { QueueExecutionResponse } from '@/shared/Executions/models/QueueExecutionResponse'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { AnalysisWizardStepType } from '../models/AnyalsisWizardStepType'
  import { CodeOutlined } from '@ant-design/icons-vue'
  import {
    ExecutionApiService,
    IExecutionApiService
  } from '@/shared/Executions/services/ExecutionApiService'
  import ExecutionStatusComponent from '@/shared/Executions/components/ExecutionStatusComponent.vue'

  export interface IPostProcessingStepProps {
    initialContentType?: 'plots' | 'tables'
    plots?: Array<Plot>
    tables?: Array<Table>
    postProcessingExecution?: Execution
    analysisExecution?: Execution
    customCalculationExecution?: Execution
    analysisFile?: AplosFile
    cleanedFile?: AplosFile
    pkResultsFile?: AplosFile
    pkResultsCustomCalcsFile?: AplosFile
    wizardModel?: AnalysisWizardModel
  }

  const props = defineProps<IPostProcessingStepProps>()

  const wizardNavigationService: AnalysisWizardNavigationService = InjectionService.injectStrict(
    AnalysisWizardNavigationService.injectableKey
  )

  const executionApiService: IExecutionApiService = InjectionService.injectStrict(
    ExecutionApiService.injectableKey
  )

  const auth = useUser()

  const isProcessingPostProcessing = ref<boolean>(false)
  const isDownloadingResults = ref<boolean>(false)

  const contentTypes = ref<Array<{ value: 'plots' | 'tables'; label: string }>>([
    { value: 'plots', label: 'Plots' },
    { value: 'tables', label: 'Tables' }
  ])

  const emit = defineEmits<{
    (e: 'update:plots', value: Array<Plot>): void
    (e: 'update:postProcessingExecution', value?: Execution): void
    (e: 'update:tables', value: Array<Table>): void
    (e: 'update:pkResultsFile', value?: AplosFile): void
    (e: 'update:pkResultsCustomCalcsFile', value?: AplosFile): void
  }>()

  const selectedContent = ref<'plots' | 'tables'>(contentTypes.value[0].value)
  const showJsonManagement = ref(false)
  const executionStatusComponent = ref<InstanceType<typeof ExecutionStatusComponent> | null>(null)

  const executionName = ref<string>(props.postProcessingExecution?.name ?? '')
  const execution = ref<Execution | undefined>(props.postProcessingExecution)

  // Watch for changes to the `postProcessingExecution` prop and update `executionName` only if it hasn't been reset manually.
  watch(
    () => props.postProcessingExecution,
    (newExecution) => {
      execution.value = newExecution
      if (newExecution?.name) {
        executionName.value = newExecution.name
      }
    },
    { immediate: true, deep: true }
  )

  watch(
    () => props.postProcessingExecution,
    (val) => {
      execution.value = val
    },
    { immediate: true }
  )

  watch(
    selectedContent,
    (oldVal, newVal) => {
      if (oldVal !== newVal) {
        showJsonManagement.value = false
      }
    },
    { deep: false }
  )

  //@ts-ignore
  const onExecutionStatusChanged = (status: ExecutionStatusType, ex: Execution) => {
    execution.value = ex
    emit('update:postProcessingExecution', ex)
  }

  const onExecutionCompleted = async (success: boolean, ex: Execution) => {
    execution.value = ex
    emit('update:postProcessingExecution', ex)
    isProcessingPostProcessing.value = false
    if (success) {
      updateWizardStepStatus(WizardStepStatusType.Good, 'Good')
    } else {
      message.error('An error was encountered generating plots/tables.')
      updateWizardStepStatus(WizardStepStatusType.Errors, 'Error Generating Plots/Tables')
    }
  }

  const updateWizardStepStatus = (statusType?: WizardStepStatusType, statusMessage?: string) => {
    if (props.wizardModel != null) {
      const stepStatusType =
        statusType != null
          ? statusType
          : props.wizardModel.sharedModel?.postProcessingExecution?.id == null
            ? WizardStepStatusType.Warnings
            : WizardStepStatusType.Good

      let stepStatusMessage = statusMessage

      if (
        stepStatusMessage == null &&
        props.wizardModel.sharedModel?.postProcessingExecution?.id == null
      ) {
        stepStatusMessage = 'Post Processing not executed'
      }

      wizardNavigationService.trySetWizardActiveStepStatus(
        props.wizardModel,
        stepStatusType,
        stepStatusMessage
      )
    }
  }

  const onSubmitPostProcessingForGenerationClicked = async () => {
    if (props.wizardModel && execution.value?.id != null) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Re-executing Post Proccessing will reset any Reports Configuration.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          submitPostProcessingForGeneration()
        },
        onCancel() {}
      })
    } else {
      submitPostProcessingForGeneration()
    }
  }

  const submitPostProcessingForGeneration = async () => {
    isProcessingPostProcessing.value = true

    try {
      const auth = useUser()
      await auth.initialize()

      const parentExecutionId = props.customCalculationExecution?.id ?? props.analysisExecution?.id!

      let queuePlotsArgs: QueuePlotsArgs | undefined = undefined
      if (props.plots?.length) {
        const flattendPlots = PlottingService.getFlattendPlots(props.plots)

        queuePlotsArgs = PlottingService.createQueuePlotsArgs(
          parentExecutionId,
          flattendPlots,
          executionName.value // Use the persistent executionName
        )

        emit('update:plots', queuePlotsArgs.plots)
      }

      let queueTablesArgs: QueueTablesArgs | undefined = undefined
      if (props.tables?.length) {
        const flattendTables = PlottingService.getFlattendPlots(props.tables)

        queueTablesArgs = TablesService.createQueueTablesArgs(
          parentExecutionId,
          flattendTables,
          executionName.value // Use the persistent executionName
        )

        emit('update:tables', queueTablesArgs.tables)
      }

      const qeueueExecutionArgs = new QueueExecutionArgs({
        tenantId: auth?.user?.tenant_id as string,
        userId: auth?.user?.id as string,
        plotArgs: queuePlotsArgs,
        tableArgs: queueTablesArgs
      })

      const response = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Generating Plots/Tables',
        props.wizardModel,
        executionApiService.queueExecution.bind(executionApiService),
        qeueueExecutionArgs
      )

      const processPlotsResponse = response?.data as QueueExecutionResponse
      if (!processPlotsResponse?.executionId) throw Error('An unexpected error has occurred!')
      const successMessage = 'Generating Plots/Tables.'
      /* processRulesResponse?.message?.length
            //? processRulesResponse.message
            : 'Post Processing queued for processing' */
      message.success(successMessage, defaultMessageDurationSeconds)

      execution.value = {
        id: processPlotsResponse?.executionId,
        status: ExecutionStatusType.Queued,
        startedUtc: new Date().toISOString()
      } as Execution

      // Emit initial execution step so implementor (wizard) is updated
      emit('update:postProcessingExecution', execution.value)

      nextTick(() => {
        executionStatusComponent.value?.startPollingStatus()
      })
    } catch (error: any) {
      isProcessingPostProcessing.value = false
      const errors: Array<string> = error?.response?.data?.errors?.map((x: any) => x.error) || [
        'No specific errors encountered.'
      ]
      Modal.error({
        title: 'Error',
        content: h('div', { style: { marginTop: '5px' } }, [
          h(
            'p',
            { style: { marginBottom: '10px' } },
            'An error(s) was encountered generating plots/tables:'
          ),
          h(
            'ul',
            errors.map((err) => h('li', { style: { marginTop: '5px' } }, err))
          )
        ]),
        okText: 'Ok',
        onOk() {}
      })
    }
  }

  const downloadResultsButtonDisabled = computed(
    () =>
      isProcessingPostProcessing.value ||
      execution.value?.id == null ||
      execution.value?.status !== ExecutionStatusType.Succeeded
  )

  const downloadResults = async () => {
    try {
      isDownloadingResults.value = true
      await auth.initialize()
      const args = new GetExecutionOutputPackageArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: execution.value?.id as string
      })
      const response = await executionApiService.getExecutionOutputPackage(args)
      if (!response.downloadUrl)
        throw Error('Invalid downloadUrl encountered onDownloadActionClicked')
      await downloadFile(response.downloadUrl, extractFilename(response.downloadUrl))
    } catch (error) {
      console.log(error)
      toast.error(`An error occurred trying to download the output package.`)
    } finally {
      isDownloadingResults.value = false
    }
  }

  const goToWizardStepByType = (stepType: AnalysisWizardStepType) => {
    if (props.wizardModel != null) {
      wizardNavigationService.setActiveWizardStepByType(stepType, props.wizardModel)
    }
  }

  const goToReportsDisabled = computed(() => {
    const shared = props.wizardModel?.sharedModel
    if (shared) {
      return !wizardNavigationService.getIsStepAvailable(AnalysisWizardStepType.Reports, shared)
        ?.isAvailable
    }
    return true
  })

  const { is2XLargeTierOrLess } = useWindowSize()

  // Reusable slot content as a small functional component (no new file)
  const WizardContentSelect = () =>
    h('div', { class: 'shadow-sm' }, [
      h('div', { class: 'mb-3' }, [
        h('label', { class: 'mb-1 block text-sm font-medium' }, 'Active Config'),
        h('div', { class: 'mb-2 flex items-center gap-2' }, [
          h(
            Select,
            {
              value: selectedContent.value,
              'onUpdate:value': (val: 'plots' | 'tables') => (selectedContent.value = val),
              placeholder: 'Active Config',
              size: 'large',
              class: 'w-full'
            },
            {
              default: () =>
                contentTypes.value.map((option) =>
                  h(SelectOption, { key: option.value, value: option.value }, () => option.label)
                )
            }
          )
        ])
      ])
    ])
</script>

<template>
  <Plotting
    v-if="selectedContent === 'plots'"
    :analysis-selection-disabled="true"
    :plots="plots"
    :postProcessingExecution="postProcessingExecution"
    :analysis-execution="analysisExecution"
    :custom-calculation-execution="customCalculationExecution"
    :analysis-file="analysisFile"
    :cleaned-file="cleanedFile"
    :pk-results-file="pkResultsFile"
    :pk-results-custom-calcs-file="pkResultsCustomCalcsFile"
    :wizard-model="wizardModel"
    :show-json-management="showJsonManagement"
    @update:show-json-management="
      (val: boolean) => {
        showJsonManagement = val
      }
    "
    @update:plots="(value: Array<Plot>) => emit('update:plots', value)"
    @update:post-processing-execution="
      (value?: Execution) => emit('update:postProcessingExecution', value)
    "
    @update:pk-results-file="(value?: AplosFile) => emit('update:pkResultsFile', value)"
    @update:pk-results-custom-calcs-file="
      (value?: AplosFile) => emit('update:pkResultsCustomCalcsFile', value)
    "
  >
    <template #wizardContentSelect>
      <component :is="WizardContentSelect" />
    </template>

    <template #wizardNavigation>
      <div class="flex items-center gap-1">
        <!-- Post Processing Wizard Plots/Tables component select -->

        <div class="mr-3">
          <ExecutionStatusComponent
            :execution="execution"
            :show-run-time="true"
            ref="executionStatusComponent"
            @notify-execution-status-changed="onExecutionStatusChanged"
            @notify-execution-complete="onExecutionCompleted"
          />
        </div>

        <!-- Download Results -->
        <a-tooltip>
          <template #title>
            {{
              !execution?.id || (!plots?.length && !tables?.length)
                ? 'Generate Plots/Tables to download results.'
                : 'Download Results'
            }}
          </template>
          <Button
            v-if="is2XLargeTierOrLess"
            type="primary"
            shape="circle"
            size="large"
            class="flex items-center justify-center"
            :disabled="downloadResultsButtonDisabled"
            @click="downloadResults"
          >
            <span class="ant-button-flex-content">
              <Loader2 v-if="isDownloadingResults" class="size-4 animate-spin" />
              <Download
                v-else
                class="size-5"
                stroke-width="1.5"
                :class="{
                  color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                }"
              />
            </span>
          </Button>

          <Button
            v-else
            type="primary"
            :disabled="downloadResultsButtonDisabled"
            @click="downloadResults"
          >
            <span class="ant-button-flex-content">
              <Loader2 v-if="isDownloadingResults" class="mr-2 size-4 animate-spin" />
              <Download
                v-else
                class="size-5"
                stroke-width="1.5"
                :class="{
                  color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                }"
              />
              {{ 'Download Results' }}
            </span>
          </Button>
        </a-tooltip>

        <a-tooltip
          :open="
            analysisExecution == null ||
            (!plots?.length && !tables?.length) ||
            isProcessingPostProcessing
              ? false
              : undefined
          "
        >
          <template #title>
            {{
              analysisExecution == null || (!plots?.length && !tables?.length)
                ? 'Select an analysis and add items to Generate Plots/Tables.'
                : !isProcessingPostProcessing
                  ? 'Generate Plots/Tables.'
                  : 'Generating Plots/Tables'
            }}
          </template>
          <Button
            v-if="is2XLargeTierOrLess"
            type="primary"
            shape="circle"
            size="large"
            class="flex items-center justify-center"
            :disabled="
              analysisExecution == null ||
              (!plots?.length && !tables?.length) ||
              isProcessingPostProcessing
            "
            @click="onSubmitPostProcessingForGenerationClicked"
          >
            <Loader2 v-if="isProcessingPostProcessing" class="size-4 animate-spin" />
            <FileCogIcon v-else class="h-4 w-4" />
          </Button>
          <Button
            v-else
            type="primary"
            :disabled="
              analysisExecution == null ||
              (!plots?.length && !tables?.length) ||
              isProcessingPostProcessing
            "
            @click="onSubmitPostProcessingForGenerationClicked"
          >
            <span class="ant-button-flex-content">
              <Loader2 v-if="isProcessingPostProcessing" class="size-4 animate-spin" />
              <FileCogIcon v-else class="h-4 w-4" />
              {{ 'Generate Plots/Tables' }}
            </span>
          </Button>
        </a-tooltip>

        <!-- View/Export JSON-->
        <a-tooltip placement="left">
          <template #title>
            {{ 'View/Export JSON' }}
          </template>
          <Button
            v-if="is2XLargeTierOrLess"
            type="primary"
            shape="circle"
            size="large"
            class="flex items-center justify-center"
            @click="showJsonManagement = true"
            title="View/Export JSON"
            :disabled="
              (!analysisExecution && !plots?.length && !tables?.length) ||
              isProcessingPostProcessing
            "
          >
            <CodeOutlined class="h-4 w-4" />
          </Button>
          <Button
            v-else
            :type="analysisExecution && !plots?.length && !tables?.length ? 'button' : 'default'"
            :class="{
              'small-button': analysisExecution && !plots?.length && !tables?.length
            }"
            @click="showJsonManagement = true"
            title="View/Export JSON"
            :disabled="
              (!analysisExecution && !plots?.length && !tables?.length) ||
              isProcessingPostProcessing
            "
          >
            <span class="ant-button-flex-content">
              <CodeOutlined class="h-4 w-4" />
              {{ 'View/Export JSON' }}
            </span>
          </Button>
        </a-tooltip>

        <!-- Go to Reports -->
        <a-tooltip placement="left" v-if="props.wizardModel != null">
          <template #title>Go to Reports</template>
          <Button
            v-if="is2XLargeTierOrLess"
            type="primary"
            shape="circle"
            size="large"
            class="flex items-center justify-center"
            :disabled="goToReportsDisabled"
            @click="
              () => {
                goToWizardStepByType(AnalysisWizardStepType.Reports)
              }
            "
          >
            <BookText class="h-4 w-4" />
          </Button>
          <Button
            v-else
            type="primary"
            :disabled="goToReportsDisabled"
            @click="
              () => {
                goToWizardStepByType(AnalysisWizardStepType.Reports)
              }
            "
          >
            <span class="ant-button-flex-content">
              {{ 'Reports' }}
              <ChevronRight class="h-4 w-4" />
            </span>
          </Button>
        </a-tooltip>
      </div>
    </template>
  </Plotting>
  <Tables
    v-else-if="selectedContent === 'tables'"
    :analysis-selection-disabled="true"
    :tables="tables"
    :postProcessingExecution="postProcessingExecution"
    :analysis-execution="analysisExecution"
    :custom-calculation-execution="customCalculationExecution"
    :analysis-file="analysisFile"
    :cleaned-file="cleanedFile"
    :pk-results-file="pkResultsFile"
    :pk-results-custom-calcs-file="pkResultsCustomCalcsFile"
    :wizard-model="wizardModel"
    :show-json-management="showJsonManagement"
    @update:show-json-management="
      (val: boolean) => {
        showJsonManagement = val
      }
    "
    @update:tables="(value: Array<Table>) => emit('update:tables', value)"
    @update:post-processing-execution="
      (value?: Execution) => emit('update:postProcessingExecution', value)
    "
    @update:pk-results-file="(value?: AplosFile) => emit('update:pkResultsFile', value)"
    @update:pk-results-custom-calcs-file="
      (value?: AplosFile) => emit('update:pkResultsCustomCalcsFile', value)
    "
  >
    <template #wizardContentSelect>
      <component :is="WizardContentSelect" />
    </template>

    <template #wizardNavigation>
      <div class="flex items-center gap-1">
        <!-- Post Processing Wizard Plots/Tables component select -->

        <div class="mr-3">
          <ExecutionStatusComponent
            :execution="execution"
            :show-run-time="true"
            ref="executionStatusComponent"
            @notify-execution-status-changed="onExecutionStatusChanged"
            @notify-execution-complete="onExecutionCompleted"
          />
        </div>

        <!-- Download Results -->
        <a-tooltip>
          <template #title>
            {{
              !execution?.id || (!plots?.length && !tables?.length)
                ? 'Generate Plots/Tables to download results.'
                : !isProcessingPostProcessing
                  ? 'Download Results'
                  : 'Generating Plots/Tables'
            }}
          </template>
          <Button
            v-if="is2XLargeTierOrLess"
            type="primary"
            shape="circle"
            size="large"
            class="flex items-center justify-center"
            :disabled="downloadResultsButtonDisabled"
            @click="downloadResults"
          >
            <span class="ant-button-flex-content">
              <Loader2 v-if="isDownloadingResults" class="size-4 animate-spin" />
              <Download
                v-else
                class="size-5"
                stroke-width="1.5"
                :class="{
                  color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                }"
              />
            </span>
          </Button>

          <Button
            v-else
            type="primary"
            :disabled="downloadResultsButtonDisabled"
            @click="downloadResults"
          >
            <span class="ant-button-flex-content">
              <Loader2 v-if="isDownloadingResults" class="mr-2 size-4 animate-spin" />
              <Download
                v-else
                class="size-5"
                stroke-width="1.5"
                :class="{
                  color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                }"
              />
              {{ 'Download Results' }}
            </span>
          </Button>
        </a-tooltip>

        <a-tooltip>
          <template #title>
            {{
              analysisExecution == null || (!plots?.length && !tables?.length)
                ? 'Select an analysis and add items to Generate Plots/Tables.'
                : !isProcessingPostProcessing
                  ? 'Generate Plots/Tables'
                  : 'Generating Plots/Tables'
            }}
          </template>
          <Button
            v-if="is2XLargeTierOrLess"
            type="primary"
            shape="circle"
            size="large"
            class="flex items-center justify-center"
            :disabled="
              analysisExecution == null ||
              (!plots?.length && !tables?.length) ||
              isProcessingPostProcessing
            "
            @click="onSubmitPostProcessingForGenerationClicked"
          >
            <Loader2 v-if="isProcessingPostProcessing" class="size-4 animate-spin" />
            <FileCogIcon v-else class="h-4 w-4" />
          </Button>
          <Button
            v-else
            type="primary"
            :disabled="
              analysisExecution == null ||
              (!plots?.length && !tables?.length) ||
              isProcessingPostProcessing
            "
            @click="onSubmitPostProcessingForGenerationClicked"
          >
            <span class="ant-button-flex-content">
              <Loader2 v-if="isProcessingPostProcessing" class="size-4 animate-spin" />
              <FileCogIcon v-else class="h-4 w-4" />
              {{ 'Generate Plots/Tables' }}
            </span>
          </Button>
        </a-tooltip>

        <!-- View/Export JSON-->
        <a-tooltip placement="left">
          <template #title>
            {{ 'View/Export JSON' }}
          </template>
          <Button
            v-if="is2XLargeTierOrLess"
            type="primary"
            shape="circle"
            size="large"
            class="flex items-center justify-center"
            @click="showJsonManagement = true"
            title="View/Export JSON"
            :disabled="
              (!analysisExecution && !plots?.length && !tables?.length) ||
              isProcessingPostProcessing
            "
          >
            <CodeOutlined class="h-4 w-4" />
          </Button>
          <Button
            v-else
            :type="analysisExecution && !plots?.length && !tables?.length ? 'button' : 'default'"
            :class="{
              'small-button': analysisExecution && !plots?.length && !tables?.length
            }"
            @click="showJsonManagement = true"
            title="View/Export JSON"
            :disabled="
              (!analysisExecution && !plots?.length && !tables?.length) ||
              isProcessingPostProcessing
            "
          >
            <span class="ant-button-flex-content">
              <CodeOutlined class="h-4 w-4" />
              {{ 'View/Export JSON' }}
            </span>
          </Button>
        </a-tooltip>

        <!-- Go to Post Processing -->
        <a-tooltip placement="left" v-if="props.wizardModel != null">
          <template #title>Go to Post Processing</template>
          <Button
            v-if="is2XLargeTierOrLess"
            type="primary"
            shape="circle"
            size="large"
            class="flex items-center justify-center"
            :disabled="goToReportsDisabled"
            @click="
              () => {
                goToWizardStepByType(AnalysisWizardStepType.Reports)
              }
            "
          >
            <FileCheck class="h-4 w-4" />
          </Button>
          <Button
            v-else
            type="primary"
            :disabled="goToReportsDisabled"
            @click="
              () => {
                goToWizardStepByType(AnalysisWizardStepType.Reports)
              }
            "
          >
            <span class="ant-button-flex-content">
              {{ 'Reports' }}
              <ChevronRight class="h-4 w-4" />
            </span>
          </Button>
        </a-tooltip>
      </div>
    </template>
  </Tables>
</template>

<style scoped></style>
